import { useState } from "react";
import { InfoHint } from "./InfoHint";
import { ResponsiveContainer, ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine } from "recharts";
import { Strategy, Warning } from "@phosphor-icons/react";

const LEVELS = [
  { l: "Отдых", s: 5, c: "#8899a6" },
  { l: "Лёгкая", s: 9, c: "#00e676" },
  { l: "Средняя", s: 13, c: "#0084ff" },
  { l: "Тяжёлая", s: 17, c: "#ff3b30" },
];
const WD = ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"];
const recColor = (v) => (v >= 67 ? "#00e676" : v >= 34 ? "#ffea00" : "#ff3b30");

export default function WeekSimulator({ forecast }) {
  const [plan, setPlan] = useState([1, 2, 0, 2, 1, 3, 0]);
  if (!forecast) return null;
  const labels = Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() + i);
    return `${WD[d.getDay()]} ${d.getDate()}`;
  });
  const predict = (strain) => Math.max(1, Math.min(99, Math.round(
    forecast.intercept + forecast.coef_bed * forecast.avg_bed + forecast.coef_strain * strain)));
  const data = plan.map((lv, i) => ({
    day: labels[i], level: LEVELS[lv].l, strain: LEVELS[lv].s, recovery: predict(LEVELS[lv].s),
  }));
  const redDays = data.filter((d) => d.recovery < 34);
  return (
    <div className="glass-card p-5" data-testid="week-simulator">
      <div className="flex items-center gap-2 mb-1">
        <Strategy size={18} weight="fill" className="text-primary" />
        <h3 className="text-sm font-semibold">Симулятор недели<InfoHint text="Спланируйте нагрузку по дням недели и посмотрите, как план отразится на прогнозируемом восстановлении." /></h3>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Спланируйте нагрузку на 7 дней — модель (по вашим {forecast.n} ночам, при обычном отбое) покажет прогноз восстановления на утро после каждого дня.
      </p>
      <div className="grid grid-cols-7 gap-1.5 mb-4">
        {plan.map((lv, i) => (
          <div key={i} className="flex flex-col items-center gap-1" data-testid={`sim-day-${i}`}>
            <span className="text-[10px] text-muted-foreground">{labels[i]}</span>
            <button
              onClick={() => setPlan((p) => p.map((v, j) => (j === i ? (v + 1) % 4 : v)))}
              data-testid={`sim-day-btn-${i}`}
              className="w-full rounded-md py-2 text-[9px] sm:text-[10px] font-semibold transition-colors border overflow-hidden text-ellipsis whitespace-nowrap px-0.5"
              style={{ borderColor: `${LEVELS[lv].c}66`, background: `${LEVELS[lv].c}1a`, color: LEVELS[lv].c }}>
              {LEVELS[lv].l}
            </button>
            <span className="num text-xs font-bold" style={{ color: recColor(data[i].recovery) }}>{data[i].recovery}%</span>
          </div>
        ))}
      </div>
      <ResponsiveContainer width="100%" height={160}>
        <ComposedChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(220 10% 16%)" vertical={false} />
          <XAxis dataKey="day" stroke="hsl(220 10% 40%)" fontSize={10} tickLine={false} axisLine={false} />
          <YAxis domain={[0, 100]} stroke="hsl(220 10% 40%)" fontSize={11} tickLine={false} axisLine={false} width={30} />
          <Tooltip contentStyle={{ background: "hsl(220 10% 12%)", border: "1px solid hsl(220 10% 22%)", borderRadius: 8, fontSize: 12 }} />
          <ReferenceLine y={67} stroke="#00e676" strokeDasharray="4 4" strokeOpacity={0.4} />
          <ReferenceLine y={34} stroke="#ff3b30" strokeDasharray="4 4" strokeOpacity={0.4} />
          <Line type="monotone" dataKey="recovery" name="Прогноз восст., %" stroke="#0084ff" strokeWidth={2}
            dot={(p) => <circle key={p.index} cx={p.cx} cy={p.cy} r={4} fill={recColor(p.payload.recovery)} />} />
        </ComposedChart>
      </ResponsiveContainer>
      {redDays.length > 0 && (
        <div className="mt-3 flex items-start gap-2 rounded-md border border-[#ff3b30]/30 bg-[#ff3b30]/10 p-3" data-testid="sim-warning">
          <Warning size={16} weight="fill" className="text-[#ff3b30] shrink-0 mt-0.5" />
          <p className="text-xs leading-relaxed">
            <span className="font-semibold text-[#ff3b30]">Риск красной зоны: {redDays.map((d) => d.day).join(", ")}.</span>{" "}
            Сдвиньте тяжёлую тренировку или добавьте день отдыха перед ней.
          </p>
        </div>
      )}
      <p className="text-[10px] text-muted-foreground mt-2">Нажимайте на день, чтобы переключить уровень: Отдых → Лёгкая → Средняя → Тяжёлая.</p>
    </div>
  );
}
